import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { merge } from 'lodash-es';
import { FUSE_APP_CONFIG } from '@fuse/services/config/config.constants';
import { environment } from 'environments/environment';

type LayoutBrandingChanges = {
    theme?: string;
    logo?: string;
    watermark?: string;
    favicon?: string;
};

@Injectable({
    providedIn: 'root'
})
export class FuseConfigService {
    private _config: BehaviorSubject<any>;

    /**
     * Constructor
     */
    constructor(@Inject(FUSE_APP_CONFIG) config: any) {
        this._config = new BehaviorSubject(config);

        // Seed config from environment.theme (loaded from localStorage app-theme
        // or from /web/config via initEnvironment)
        const theme = environment.layout;
        this.config = {
            logo: theme.logo,
            theme: theme.theme,
            watermark: theme.watermark,
            favicon: theme.favicon
        };
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    set config(value: any) {
        const config = merge({}, this._config.getValue(), value);
        this._setFavicon(config.favicon);
        this._config.next(config);
    }

    get config$(): Observable<any> {
        return this._config.asObservable();
    }

    get config(): any {
        return this._config.getValue();
    }

    get theme(): string {
        return this._config.getValue().theme ?? 'default';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    reset(): void {
        this._config.next(this.config);
    }

    applyPermanentLayoutChanges(value: LayoutBrandingChanges): void {
        const config = merge({}, this._config.getValue(), value);
        this._setFavicon(config.favicon);
        this._config.next(config);
    }

    private _setFavicon(url: string): void {
        if (!url || typeof document === 'undefined') return;
        const favicon = document.getElementById('app-favicon');
        if (favicon) favicon.setAttribute('href', url);
    }
}
